import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, publishReplay, refCount } from 'rxjs/operators';
import { UrlBuilder } from '../../core';
import { TabCategory } from '../../documents';
import { SponsorRouteData } from '../sponsor-route-data';
import { CategoriesStorage } from '../../documents/categories-storage';
import { Category } from '../../documents/models/category';
import { CategoryDocumentsFilterStorage } from './category-documents-filter-storage';
import { routeParams, findStudySiteIdentities } from '../../shared/route-helpers';
import { SponsorStudySiteIdentity } from '../sites/sponsor-study-site-identity';

@Component({
    selector: 'site-documents',
    styles: [require('./site-documents.component.scss')],
    template: require('./site-documents.component.html'),
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SiteDocumentsComponent implements OnInit, OnDestroy {
    public tabs$: Observable<TabCategory[]>;

    public siteId$: Observable<string>;
    public siteIdentities: SponsorStudySiteIdentity[];
    public selectedSiteIdentity$: Observable<SponsorStudySiteIdentity>;

    public readonly filterDefinitions$ = this.filterStorage.filterFields;
    public readonly filterGroup = 'sponsor-site-documents-view-group';

    private subscription = new Subscription();

    constructor(
        private readonly route: ActivatedRoute,
        private readonly routeData: SponsorRouteData,
        private readonly urlBuilder: UrlBuilder,
        private readonly categoriesStorage: CategoriesStorage,
        private readonly filterStorage: CategoryDocumentsFilterStorage,
    ) { }

    public ngOnInit() {
        this.siteId$ = this.getSiteId();
        this.siteIdentities = findStudySiteIdentities(this.route.snapshot);
        this.subscription.add(
            this.redirectToFirstSiteIfEmptySiteId()
        );
        this.selectedSiteIdentity$ = this.getSelectedSiteIdentity();
        this.tabs$ = this.createTabs();
    }

    public ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    public onSiteChanged(site: SponsorStudySiteIdentity) {
        this.navigateToSite(site);
    }

    private getSiteId(): Observable<string> {
        return this.route.params.pipe(
            map(params => params[routeParams.siteId] as string),
            distinctUntilChanged(),
            publishReplay(1),
            refCount()
        );
    }

    private getSelectedSiteIdentity(): Observable<SponsorStudySiteIdentity> {
        return this.siteId$
            .pipe(
                map(siteId => this.siteIdentities.find(t => t.siteId.toString() === siteId)),
                filter(t => !!t),
                publishReplay(1),
                refCount()
            );
    }

    private redirectToFirstSiteIfEmptySiteId(): Subscription {
        return this.siteId$
            .subscribe(siteId => {
                const site = this.siteIdentities.find(t => t.siteId.toString() === siteId);
                if (!site) {
                    this.navigateToSite(this.siteIdentities[0]);
                }
            });
    }

    private navigateToSite(site: SponsorStudySiteIdentity) {
        const categoryId = this.route.snapshot.params[routeParams.childCategoryId];

        this.urlBuilder
            .esa()
            .sponsor()
            .study(this.routeData.snapshot.selectedStudy)
            .siteDocuments(site, categoryId)
            .navigate({ preserveQueryParams: true, navigationExtras: { replaceUrl: true } });
    }

    private createTabs(): Observable<TabCategory[]> {
        const allowedCategories$ = combineLatest(
            this.routeData.studyPermissions,
            this.categoriesStorage.siteDocumentsCategories
        ).pipe(
            map(([permissions, categories]) => categories.filter(c => permissions.canViewCategory(c.categoryId))),
        );

        return combineLatest(
            [this.routeData.selectedStudy,
            this.selectedSiteIdentity$,
            allowedCategories$]
        ).pipe(
            map(([study, site, categories]) => {
                const tabs = categories
                    .map((category: Category) => {
                        const url = this.urlBuilder
                            .esa()
                            .sponsor()
                            .study(study)
                            .siteDocuments(site, category.categoryId)
                            .url();

                        return { name: this.getTabName(category), url } as TabCategory;
                    });

                return tabs;
            }),
            publishReplay(1),
            refCount()
        );
    }

    private getTabName(category: Category) {
        if (category.categoryId === 'site-actions') {
            return 'Actions';
        }
        if (category.categoryId === 'site-communications') {
            return 'Communications';
        }
        return category.name;
    }
}
